import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { toast } from 'vue-sonner';
import { clearStatus, getStatus, isRunning, isTerminal, startImport, type ImportRunState } from '@api/bulkImport';
import { useUserStore } from '@store/user';

/**
 * How often a running import is polled.
 *
 * Progress arrives over the websocket — the server broadcasts every state
 * transition — so this is only the fallback for when the socket is down or the
 * event was missed. It is deliberately slow: we host on Vapor and pay per
 * request, and an import that ran for a few minutes at the old half-second
 * cadence cost hundreds of them per operator to learn what the socket delivers
 * for free.
 */
const POLL_MS = 5000;

/** Payload of the server's BulkImportProgressed broadcast. */
interface ProgressEvent {
    model: string;
    state: ImportRunState;
}

/**
 * Run state for bulk imports, held here rather than in the modal so a run
 * survives the modal closing and the operator navigating away.
 *
 * The server holds the authoritative state for six hours, so nothing needs
 * persisting to the browser: a reload simply asks again.
 */
export const useBulkImportStore = defineStore('bulkImport', () => {
    /** Latest known state per model key, e.g. "containers". */
    const runs = ref<Record<string, ImportRunState | null>>({});

    /** Bumped whenever a model's import succeeds, so an index can refresh. */
    const completedAt = ref<Record<string, number>>({});

    const timers: Record<string, ReturnType<typeof setInterval> | undefined> = {};

    /**
     * Highest state sequence applied per model.
     *
     * The two ways a state arrives — broadcast and poll — race each other, and
     * broadcast delivery is queued and so unordered. Without this a slow poll
     * response resolving after a newer event would walk the progress bar
     * backwards, or reinstate "importing" over a finished run.
     */
    const sequences: Record<string, number | undefined> = {};

    /**
     * Bumped by teardown(). A request already in flight cannot be cancelled —
     * stopPolling() only clears the interval — so a poll dispatched just before
     * logout would resolve afterwards and write the state teardown had just
     * cleared, putting the previous user's run back. Every await here captures
     * this first and drops its result if it no longer matches.
     */
    let generation = 0;

    function stateFor(model: string): ImportRunState | null {
        return runs.value[model] ?? null;
    }

    const runningModels = computed(() => Object.keys(runs.value).filter((model) => isRunning(runs.value[model])));

    function running(model: string): boolean {
        return isRunning(stateFor(model));
    }

    function finished(model: string): boolean {
        return isTerminal(stateFor(model));
    }

    /**
     * Record a state, from whichever source produced it, unless it is stale.
     *
     * The single place a run's state is written, so the sequence guard and the
     * stop-and-announce rules cannot be bypassed by one path forgetting them.
     */
    function apply(model: string, next: ImportRunState): void {
        const seen = sequences[model];
        const arriving = next.sequence;

        // A cleared run holds Infinity, so a broadcast still in flight for the
        // run just dismissed cannot put it back on screen.
        //
        // A state with no sequence is authoritative: only { status: null } —
        // the server having forgotten the run — answers without one.
        if (arriving !== undefined && seen !== undefined && arriving <= seen) {
            return;
        }

        if (arriving !== undefined) {
            sequences[model] = arriving;
        }

        const wasRunning = isRunning(runs.value[model]);
        runs.value = { ...runs.value, [model]: next };

        // Anything not running is worth stopping for, not just a terminal
        // result: once the run's state has expired from the cache the
        // server answers { status: null }, which is neither running nor
        // terminal, and polling on it would never stop.
        if (!isRunning(next)) {
            stopPolling(model);
        }

        // Only announce a result the operator was not already watching
        // finish, so the toast marks a transition rather than repeating
        // what the modal is already showing.
        if (isTerminal(next) && wasRunning) {
            announce(model, next);
        }
    }

    async function refresh(model: string): Promise<void> {
        const startedIn = generation;

        try {
            const next = await getStatus(model);

            // Torn down while this was in flight: the session it belongs to is
            // over, so the answer is no longer ours to record.
            if (startedIn !== generation) {
                return;
            }

            apply(model, next);
        } catch {
            // A failed poll is not a failed import. The run is server-side and
            // will still be there on the next tick.
        }
    }

    function announce(model: string, state: ImportRunState): void {
        if (state.status === 'succeeded') {
            completedAt.value = { ...completedAt.value, [model]: Date.now() };
            toast.success(state.message ?? 'Import complete.');

            return;
        }

        toast.error(state.message ?? 'The import did not finish.');
    }

    function startPolling(model: string): void {
        stopPolling(model);
        listen();

        timers[model] = setInterval(() => refresh(model), POLL_MS);
    }

    function stopPolling(model: string): void {
        if (timers[model]) {
            clearInterval(timers[model]);
            timers[model] = undefined;
        }
    }

    /**
     * The user channel this store is bound to, or null when unbound. Held so
     * unlisten() unsubscribes the same channel it subscribed, even if the
     * signed-in user has changed in between.
     */
    let boundChannel: string | null = null;

    /**
     * Named so unlisten() unbinds only this store's handler. stopListening
     * without one removes every binding for the event on a channel shared with
     * notifications and staff assignment, which would deafen them too.
     */
    const handleProgress = (event: ProgressEvent) => {
        if (event?.model && event.state) {
            apply(event.model, event.state);
        }
    };

    /** Subscribe to the operator's own channel, where run progress arrives. */
    function listen(): void {
        const userId = useUserStore().getUser?.id;

        if (!window.echo || !userId) {
            return;
        }

        const channel = `App.Models.User.${userId}`;

        if (boundChannel === channel) {
            return;
        }

        unlisten();

        try {
            window.echo.private(channel).listen('.BulkImportProgressed', handleProgress);
            boundChannel = channel;
        } catch {
            // No socket: the poll fallback still reports the run, so this is
            // slower rather than broken.
        }
    }

    function unlisten(): void {
        if (!boundChannel) {
            return;
        }

        try {
            window.echo?.private(boundChannel).stopListening('.BulkImportProgressed', handleProgress);
        } catch {
            // Already gone. Nothing to unbind.
        }

        boundChannel = null;
    }

    /** Queue an import of a workbook already uploaded to S3. */
    async function start(model: string, key: string): Promise<void> {
        const startedIn = generation;

        // A new run restarts the server's sequence at 1, so the previous run's
        // high-water mark would reject every state this one produces.
        sequences[model] = undefined;

        const next = await startImport(model, key);

        // Same race as refresh(): the import is queued server-side and will run
        // regardless, but a session that has ended must not adopt it.
        if (startedIn !== generation) {
            return;
        }

        startPolling(model);
        apply(model, next);
    }

    /**
     * Forget a finished run. Local state goes first so the interface resets at
     * once; the server is told afterwards and its answer is not needed.
     */
    function clear(model: string): void {
        stopPolling(model);

        // Refuse anything further for this run. The server broadcasts each
        // transition, and one dispatched just before this can still be in
        // flight — without this it would arrive and reopen the result the
        // operator has just dismissed.
        sequences[model] = Infinity;

        runs.value = { ...runs.value, [model]: null };
        clearStatus(model).catch(() => undefined);
    }

    /**
     * How long after finishing a run is still worth showing to someone who has
     * just loaded the page.
     */
    const RESULT_IS_NEWS_FOR_MS = 5 * 60 * 1000;

    /**
     * Pick up a run that was already going, on first load of a page that cares.
     *
     * A run still in flight is adopted and polled. A run that has already
     * finished is adopted only while the result is still news: a recent
     * failure is worth showing, because the file created nothing and the reason
     * lives here. Anything older is discarded rather than greeting the operator
     * with a result from hours ago that the index already reflects.
     */
    async function attach(model: string): Promise<void> {
        // Before asking, not after: a run adopted here may be mid-flight, and
        // subscribing first means no transition falls in the gap between the
        // status answer and the listener being bound.
        listen();

        await refresh(model);

        if (running(model)) {
            startPolling(model);

            return;
        }

        const current = stateFor(model);

        if (!isTerminal(current)) {
            return;
        }

        const finishedAt = current?.updated_at ? Date.parse(current.updated_at) : NaN;
        const isNews =
            current?.status === 'failed' &&
            Number.isFinite(finishedAt) &&
            Date.now() - finishedAt < RESULT_IS_NEWS_FOR_MS;

        if (!isNews) {
            clear(model);
        }
    }

    /**
     * Stop watching every run, on the way out of the session.
     *
     * The runs themselves are left alone. An import is a queued job on the
     * server, so cancelling one because the operator logged out would leave a
     * fleet half created — and the result is held for six hours, to be found
     * waiting on the next login. That is also why this is not clear(): clear()
     * calls clearStatus(), which tells the server to forget the run.
     *
     * Local state goes, though. Server-side a run is keyed to the user who
     * started it, and this map is not — so on a shared machine the next person
     * to log in would see the previous one's progress until the first refresh
     * replaced it.
     */
    function teardown(): void {
        generation += 1;
        Object.keys(timers).forEach(stopPolling);
        Object.keys(sequences).forEach((model) => delete sequences[model]);
        unlisten();
        runs.value = {};
        completedAt.value = {};
    }

    return {
        runs,
        completedAt,
        runningModels,
        stateFor,
        running,
        finished,
        refresh,
        start,
        clear,
        attach,
        teardown,
    };
});
